import { composeVerdict } from '@/lib/grading/verdict';
import { MILESTONES } from '@/seed/milestones';
import { personaKeyToId } from '@/constants/avatars';
import type { MilestoneEvent } from '@/lib/grading/types';
import type { CBClientData } from '@/lib/codebaby/types';
import type { Milestone, SessionMoment, SessionOutcome, TranscriptTurn } from '@/types/session';
import type { ScenarioConfig } from '@/types/scenario';

/**
 * gradeSession — pure function from (transcript, scenario, per-turn classifier
 * output) to a verdict the UI renders. It now DELEGATES to the vertical-pack
 * grader (`composeVerdict` + `src/seed/milestones.ts`): the classifier emits
 * the same rich milestone tags the pack defines, so milestones actually get
 * credited. (The old generic vocabulary — discovery/pain_quantified/… — never
 * matched the classifier's tags, so `milestones_hit` was always empty and the
 * debrief showed no strengths.)
 */

export interface SessionVerdict {
  score: number;
  outcome: SessionOutcome;
  /** Rich vertical-pack milestone tags actually credited this session. */
  milestones_hit: Milestone[];
  moments: SessionMoment[];
  final_temperature: number;
  duration_s: number;
}

/** Valid milestone tags for this vertical — guards against null / stale tags. */
const KNOWN_TAGS = new Set<string>(Object.keys(MILESTONES));

/**
 * Coach's spoken take for a moment card. The classifier's `moment_summary` is a
 * flat observation ("Agent asks a genuine discovery question") — on its own it
 * just restates the card headline. Here we turn it into actual coaching: the
 * observation, then WHY it moved (or failed to move) the room and what to do
 * with it next time. Framed off the win/growth signal so wins get reinforced
 * and growths get a concrete fix. (The deeper upgrade is to have the meta
 * classifier author this per-turn; this composes a useful take without a
 * classifier round-trip.)
 */
function composeCoachNote(
  observation: string,
  tag: Milestone | null,
  momentType: 'win' | 'growth',
): string {
  const skill = tag ? MILESTONES[tag].label.toLowerCase() : null;
  if (momentType === 'win') {
    return skill
      ? `${observation} That's "${skill}" done right — a concrete, earned move, not a slogan, which is exactly what shifts a skeptic toward yes. Lock it in and lead with it earlier next time.`
      : `${observation} That's a concrete, earned move — exactly the kind of specificity that shifts a skeptic toward yes. Lead with it earlier next time.`;
  }
  return skill
    ? `${observation} You reached for "${skill}" but it landed soft — make it concrete and checkable (a number, a named step, a specific protocol) so it can't be waved off, and it converts into a real win.`
    : `${observation} The instinct was right but it landed soft — make it concrete and checkable so it can't be waved off, and it converts into a real win.`;
}

export function gradeSession(
  transcript: TranscriptTurn[],
  scenario: ScenarioConfig,
  /**
   * Per-turn classifications from the meta classifier. Entry N pairs with the
   * rep turn that prompted persona response N. Each carries the milestone_tag
   * the classifier credited (a vertical-pack tag), the moment type, and the
   * temperature delta the live gauge already used.
   *
   * NOTE: in LIVE mode this history is lossy — RoleplayScreen records each
   * classification by asynchronously replacing the last entry, and aborts the
   * previous turn's classifier when a new turn starts. Classifications can land
   * out of order, overwrite the wrong turn, or never arrive. It's reliable
   * enough for the "moments" list (best-effort) but NOT for milestone
   * accounting. For that we prefer `creditedMilestones` below.
   */
  clientDataHistory: CBClientData[] = [],
  /**
   * Authoritative, race-proof stream of every milestone the classifier
   * credited this session — accumulated synchronously in RoleplayScreen as
   * each classification lands (the same path that feeds `alreadyHit`). This is
   * the source of truth for `milestones_hit`, score, and outcome. Empty in mock
   * mode, where `clientDataHistory` (appended once per turn, no race) is
   * reliable — so we MERGE both sources and dedupe by tag.
   */
  creditedMilestones: MilestoneEvent[] = [],
  /**
   * The final temperature the live gauge showed (0..4). Preferred over
   * re-summing `clientDataHistory` deltas, which the live race can drop.
   */
  finalTemperatureOverride?: number,
): SessionVerdict {
  const learnerTurns = transcript.filter((t) => t.speaker === 'learner');

  // 1. Milestone events — merge the authoritative live stream with anything
  //    recoverable from clientDataHistory (mock + best-effort live), deduped
  //    by tag so a milestone is counted once regardless of how many sources
  //    saw it.
  const events: MilestoneEvent[] = [];
  const seenTags = new Set<string>();
  const pushEvent = (e: MilestoneEvent) => {
    if (!KNOWN_TAGS.has(e.tag) || seenTags.has(e.tag)) return; // ignore null/out-of-vocab + dupes
    seenTags.add(e.tag);
    events.push(e);
  };
  for (const e of creditedMilestones) pushEvent(e); // authoritative first
  for (let i = 0; i < clientDataHistory.length; i += 1) {
    const cd = clientDataHistory[i];
    const tag = cd?.milestone_tag;
    if (!tag) continue;
    pushEvent({
      tag: tag as MilestoneEvent['tag'],
      turnId: String(i),
      producerText: learnerTurns[i]?.text ?? '',
      isKeyMoment: Boolean(cd?.is_key_moment),
      momentType: cd?.moment_type ?? null,
    });
  }

  // 2. Final temperature level (0..4). Prefer the live gauge value; fall back
  //    to accumulating clientDataHistory deltas (mock / older callers).
  let temperature = scenario.start_temperature;
  for (const cd of clientDataHistory) {
    if (typeof cd?.temperature_delta === 'number') temperature += cd.temperature_delta;
  }
  const final_temperature = Math.max(0, Math.min(4, finalTemperatureOverride ?? temperature));

  // 3. Verdict — the vertical-pack engine handles outcome, score, wins/growths,
  //    and missed milestones off the rich tags + their weights.
  const verdict = composeVerdict({
    personaId: personaKeyToId(scenario.persona_id),
    milestones: events,
    finalTemperatureLevel: final_temperature,
    expectedMilestones: scenario.milestones,
  });

  // 4. Moments ("things to remember") — the classifier's LLM-authored key
  //    moments, grounded in the actual exchange.
  const moments: SessionMoment[] = [];
  for (let i = 0; i < clientDataHistory.length && moments.length < 3; i += 1) {
    const cd = clientDataHistory[i];
    if (!cd?.is_key_moment || !cd.moment_type || !cd.moment_summary) continue;
    const repTurn = learnerTurns[i];
    if (!repTurn) continue;
    // Headline = the skill (milestone label) when we know it; the flat
    // observation is reserved for Coach's take so the two don't echo.
    const tag =
      cd.milestone_tag && KNOWN_TAGS.has(cd.milestone_tag) ? (cd.milestone_tag as Milestone) : null;
    moments.push({
      type: cd.moment_type,
      title: tag ? MILESTONES[tag].label : cd.moment_summary,
      quote: repTurn.text,
      at_s: repTurn.at_s,
      coach_note: composeCoachNote(cd.moment_summary, tag, cd.moment_type),
      coach_note_id: cd.coach_note_id ?? `coach-note-meta-${i}`,
    });
  }

  const duration_s = transcript.length > 0 ? Math.max(...transcript.map((t) => t.at_s)) : 0;

  return {
    score: verdict.score,
    outcome: verdict.outcome === 'closed' ? 'closed' : 'not_closed',
    milestones_hit: verdict.milestonesHit,
    moments,
    final_temperature,
    duration_s,
  };
}
